/**
 * x-epigraph-configurator directive.
 *
 * Integrates the Epigraph product configurator widget with Shopify's cart.
 * Configuration data (prices, cart messages, stock messages) is read from a
 * JSON script element identified by the section's `data-section-id` attribute.
 *
 * Responsibilities:
 * - Updates the Epigraph core API's pricing tables with Shopify variant prices
 *   and compare-at prices when the API is ready.
 * - Computes and sets the cart message based on the out-of-stock lead times
 *   of items currently in the configurator's cart.
 * - Adds the configured items to the Shopify cart when a checkout event fires.
 *
 * Configuration JSON shape (sourced from `window[ConfiguratorData_<sectionId>]`):
 * ```json
 * {
 *   "prices": { "<sku>": { "<variant>": { "price": 0, "compareAtPrice": 0, "variantId": 0 } } },
 *   "cartMessages": { "<sku>": { "<variant>": { "inventoryQuantity": "", "leadTimeMin": "", "leadTimeMax": "", "cartMessage": "" } } },
 *   "inStockMessage": "In stock",
 *   "outOfStockMessage": "Out of stock"
 * }
 * ```
 *
 * @example
 *   <div x-epigraph-configurator data-section-id="epigraph-section">…</div>
 *   <script id="ConfiguratorData_epigraph-section" type="application/json">…</script>
 */
(() => {
  const root = (
    Alpine,
    el,
    { prices, cartMessages, inStockMessage, outOfStockMessage },
  ) => {
    Alpine.bind(el, () => ({
      /** Calls `updatePricing` when the Epigraph core API is ready. */
      ["@coreApi:ready"]() {
        this.updatePricing();
      },

      /** Logs an error when the Epigraph core API fails to initialise. */
      ["@coreApi:failed"]($event) {
        this.handleApiFailure($event);
      },

      /** Recalculates the cart message when the global variant selection changes. */
      ["@globalVariant:updated"]() {
        this.handleCartMessage();
      },

      /** Recalculates the cart message when the cart items change. */
      ["@items:updated"]() {
        this.handleCartMessage();
      },

      /** Handles the Epigraph checkout event and adds items to the Shopify cart. */
      ["@cart:checkout"]($event) {
        this.addItemsToCart($event);
      },

      ["x-data"]() {
        /**
         * Safely reads a deeply nested value from an object.
         * @param {object} obj - The object to read from.
         * @param {...string} args - The path of keys to traverse.
         * @returns {*} The value at the path, or undefined.
         */
        const getNested = (obj, ...args) => {
          return args.reduce((obj, level) => obj && obj[level], obj);
        };

        /**
         * Returns the store price for a given SKU, variant, and price type.
         * @param {string} sku - The SKU identifier.
         * @param {string} variant - The variant identifier.
         * @param {"price"|"compareAtPrice"} price - The price type to retrieve.
         * @returns {number|undefined}
         */
        const getStorePriceBySkuAndVariant = (sku, variant, price) => {
          return getNested(prices, sku, variant, price);
        };

        /**
         * Updates a pricing object in place, replacing each variant's value
         * with the corresponding Shopify store price when available.
         * @param {object} object - The pricing object returned by the Epigraph core API.
         * @param {"price"|"compareAtPrice"} type - Which price type to apply.
         * @returns {object} The mutated pricing object.
         */
        const updatePricingInfo = (object, type) => {
          Object.entries(object).forEach(([sku, skuPricingObject]) => {
            Object.entries(skuPricingObject).forEach(([variant]) => {
              const storePriceBySkuAndVariant = getStorePriceBySkuAndVariant(
                sku,
                variant,
                type,
              );

              if (storePriceBySkuAndVariant) {
                object[sku][variant] = storePriceBySkuAndVariant;
              }
            });
          });

          return object;
        };

        return {
          /**
           * Fetches current pricing info from the Epigraph core API, applies
           * Shopify store prices, and pushes the updated values back to the API
           * for both regular prices and compare-at prices.
           */
          updatePricing() {
            const { core } = this.$root.api;
            const currentPricingInfo = updatePricingInfo(
              core.getCurrentPricingInfo(),
              "price",
            );
            const currentCompareAtPricing = updatePricingInfo(
              core.getCurrentCompareAtPricingInfo(),
              "compareAtPrice",
            );

            core.updatePricingForAllProducts(currentPricingInfo);
            core.updateCompareAtPricingForAllProducts(currentCompareAtPricing);
          },

          /**
           * Logs the API failure event when the Epigraph core API fails to initialise.
           * @param {CustomEvent} $event - The failure event.
           */
          handleApiFailure($event) {
            console.error("Failed to initialize core api.");
            console.error($event);
          },

          /**
           * Determines the appropriate cart message based on out-of-stock items
           * in the configurator cart. Finds the item with the longest lead time
           * and sets its message as the cart message. Falls back to the
           * `outOfStockMessage` when a stocked-out item has no lead time, or
           * `inStockMessage` when all items are in stock.
           * @returns {Promise<void>}
           */
          async handleCartMessage() {
            const { core } = this.$root.api;

            try {
              let currentCart = await core.getCartItemsAsync();
              let cartMessage = inStockMessage;
              let leadTimeMax = 0;
              let leadTimeMin = 0;

              // Process cart items to find the longest lead time for out-of-stock items.
              if (
                currentCart &&
                currentCart.cartItems &&
                currentCart.cartItems.length > 0
              ) {
                for (const item of currentCart.cartItems) {
                  const { skuID, currentVariant } = item;

                  // Check if the item exists in the epigraph messages.
                  if (
                    cartMessages[skuID] &&
                    cartMessages[skuID][currentVariant]
                  ) {
                    const productInfo = cartMessages[skuID][currentVariant];

                    const inventoryQuantity =
                      parseInt(productInfo.inventoryQuantity, 10) || 0;

                    // Check if item is out of stock.
                    if (inventoryQuantity <= 0) {
                      // Convert lead times to integers (default to 0 if empty or NaN).
                      const itemLeadTimeMin =
                        parseInt(productInfo.leadTimeMin, 10) || 0;
                      const itemLeadTimeMax =
                        parseInt(productInfo.leadTimeMax, 10) || 0;

                      // When out of stock with no lead time, fall back to the default
                      // out-of-stock message — this is the worst case, so stop here.
                      if (itemLeadTimeMin === 0 && itemLeadTimeMax === 0) {
                        cartMessage = outOfStockMessage;
                        break;
                      }

                      // Update cart message if this item has a longer lead time than seen so far.
                      if (
                        itemLeadTimeMax > leadTimeMax ||
                        (itemLeadTimeMax === leadTimeMax &&
                          itemLeadTimeMin > leadTimeMin)
                      ) {
                        if (productInfo.leadTime) {
                          cartMessage = productInfo.leadTime;
                        }

                        leadTimeMin = itemLeadTimeMin;
                        leadTimeMax = itemLeadTimeMax;
                      }
                    }
                  }
                }
              }

              // Set the cart message.
              if (cartMessage) {
                core.setCartMessage(cartMessage);
              }
            } catch (error) {
              console.error(error);
            }
          },

          /**
           * Translates the Epigraph cart items into Shopify cart line items
           * (using the Shopify variant ID from `prices`) and adds them to the
           * Shopify cart via `$ajaxCart`. Also generates and attaches a
           * shareable configuration link as the cart note.
           * @param {CustomEvent} $event - The `cart:checkout` event with `data.cartItems`.
           * @returns {Promise<void>}
           */
          async addItemsToCart($event) {
            const { core } = this.$root.api;

            const cartItems = getNested($event, "data", "cartItems");

            const body = {
              items: [],
              note: null,
            };

            cartItems.forEach((item) => {
              const variantId = getNested(
                prices,
                item.skuID,
                item.currentVariant,
                "variantId",
              );

              if (variantId) {
                const cartItem = {
                  id: variantId,
                  quantity: item.count,
                  properties: {},
                };

                const { leadTime, leadTimeMin, leadTimeMax } =
                  cartMessages[item.skuID][item.currentVariant];

                if (leadTime) {
                  cartItem.properties._lead_time = leadTime;
                  cartItem.properties._lead_time_min = leadTimeMin;
                  cartItem.properties._lead_time_max = leadTimeMax;
                }

                body.items.push(cartItem);
              }
            });

            const note = await core.generateShareableLink();

            if (note) {
              body.note = note;
            }

            await this.$ajaxCart.add(body);
          },
        };
      },
    }));
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive("epigraph-configurator", (el) => {
      const sectionId = el.dataset.sectionId;

      root(
        Alpine,
        el,
        JSON.parse(window[`ConfiguratorData_${sectionId}`].innerText),
      );
    });
  });
})();
